import { useMemo } from 'react';
import { Box, Link, Spinner, Tag, Text } from '@nimbus-ds/components';
import { ChargeDto } from '@tiendanube/common';
import { BILLING_NEW_FLOW_FREQUENCY } from 'App/featuresFlags';
import { useHasTags } from 'App/hooks';
import { useGoToPlans } from 'commons/hooks/useGoToPlans';
import { useGetIsFreePlan, useStoreStatus } from 'domains/Auth/hooks';
import CurrencyPriceWithFrequency from 'domains/Billing/Checkout/components/CurrencyPriceWithFrequency';
import {
  useAppName,
  useExpirationDate,
  useGetTotalForCharges,
} from 'domains/Billing/Checkout/hooks';
import { useGoToConceptDetails } from 'domains/Billing/Checkout/hooks/useGoToConceptDetails';
import { VIRTUAL_CHARGE_ID } from 'domains/Billing/Checkout/hooks/usePaymentMethods';
import {
  PaymentStatusTag,
  ChargePeriodDetail,
  SelectPlanRecurrency,
} from 'domains/Billing/Checkout/pages/PlansAndSubscriptions/components';
import useTranslationBilling from 'domains/Billing/useTranslationBilling';
import { TrialStatusTag } from './components';

interface ChargeToPayProps {
  charge: ChargeDto;
  isPlanChargeToUpdate?: boolean;
}

function ChargeToPay({
  charge,
  isPlanChargeToUpdate = false,
}: Readonly<ChargeToPayProps>) {
  const t = useTranslationBilling(
    'checkout.plansAndSubscriptions.paymentsTabs.toPay',
  );
  const { getChargeDiscountPercentage, getTotalForCharges } =
    useGetTotalForCharges();

  const [hasRecurrencyTag] = useHasTags(BILLING_NEW_FLOW_FREQUENCY);
  const { isTrial } = useGetIsFreePlan();
  const isVirtualCharge = charge.id === VIRTUAL_CHARGE_ID;

  const { handleGoToPlans } = useGoToPlans(true);

  const { appName, isLoading } = useAppName(
    charge.conceptCode,
    charge.metadata?.appId,
  );

  const { expiresIn } = useExpirationDate(
    charge.conceptCode,
    charge.metadata?.appId,
  );
  const { storeStatus } = useStoreStatus();
  const isPlan = charge.conceptCode === 'plan-cost';

  const goToDetails = useGoToConceptDetails(
    'chargesToPay',
    charge.conceptCode,
    charge.metadata?.appId,
    !isVirtualCharge ? charge.id : undefined,
  );

  const isPhoenixCharge =
    charge.conceptCode === 'plan-cost' && storeStatus === 'store_down';

  const shouldHideDetails =
    isPhoenixCharge ||
    (charge.conceptCode === 'app-cost' && !charge.subscription) ||
    charge.conceptCode === 'invoices-fee';

  const description =
    charge.conceptCode === 'app-extra-fee'
      ? `${appName} - ${t('extraFee')}`
      : charge.description;

  const discountPercentage = useMemo(
    () => getChargeDiscountPercentage(charge, false),
    [charge, getChargeDiscountPercentage],
  );

  return (
    <Box display="flex" flexDirection="row" justifyContent="space-between">
      <Box display="flex" flexDirection="column" gap="1">
        <Box display="flex" flexDirection="column">
          {isLoading ? (
            <Spinner size="small" />
          ) : (
            <Text fontWeight="bold">{description}</Text>
          )}
          {!isVirtualCharge && <ChargePeriodDetail {...charge} />}
        </Box>
        {isPlan && isTrial && charge.status === 'PENDING' ? (
          <TrialStatusTag paymentStatus={charge.status} />
        ) : (
          <PaymentStatusTag paymentStatus={charge.status} />
        )}
        {!!isPhoenixCharge && (
          <Box paddingTop="1">
            <Link
              fontSize="caption"
              appearance="primary"
              onClick={handleGoToPlans}
              textDecoration="none"
            >
              {t('changePlan')}
            </Link>
          </Box>
        )}
        {!shouldHideDetails && (
          <Box paddingTop="1">
            <Link
              fontSize="caption"
              appearance="primary"
              onClick={goToDetails}
              textDecoration="none"
            >
              {t('chargeDetails')}
            </Link>
          </Box>
        )}
      </Box>
      <Box
        display="flex"
        flexDirection="column"
        flex="1 1 20%"
        alignItems="flex-end"
        gap="2"
      >
        <Box display="flex" flexDirection="row" gap="1">
          {discountPercentage > 0 && (
            <Tag appearance="success">{`-${discountPercentage}%`}</Tag>
          )}
          <CurrencyPriceWithFrequency
            amountValue={getTotalForCharges([charge], {
              includeGeneralDiscounts: true,
              includePeriodDiscounts: hasRecurrencyTag,
              includeTaxes: false,
            })}
            amountCurrency={charge.amountCurrency}
            recurringInterval={
              charge.metadata?.recurringInterval ||
              charge.subscription?.recurringInterval
            }
            recurringFrequency={
              charge.metadata?.recurringFrequency ||
              charge.subscription?.recurringFrequency
            }
          />
        </Box>
        {isPlanChargeToUpdate && <SelectPlanRecurrency charge={charge} />}
        {charge.conceptCode === 'plan-cost' && storeStatus === 'store_down' && (
          <Text fontSize="caption" color="neutral-textLow">
            {t('churned.expiredPlan')}
          </Text>
        )}
        {!isTrial &&
          storeStatus !== 'store_down' &&
          !!expiresIn &&
          expiresIn > 0 && (
            <Text fontSize="caption" color="neutral-textLow">
              {t('expiresIn', { expiresIn })}
            </Text>
          )}
      </Box>
    </Box>
  );
}

export default ChargeToPay;
